import {Box, Skeleton} from '@mantine/core';
import {randomId} from '@mantine/hooks';
import Head from 'next/head';
import {useRouter} from 'next/router';
import type React from 'react';
import {useEffect, useState} from 'react';
import {TITLE_SUFFIX} from '../../../config/constants';
import {
  COMPANIES_CRUMB,
  COMPANY_FOLLOW_RANKING_CRUMB,
  HOME_CRUMB,
} from '../../../config/constants/crumb';
import {GoogleAdBannerName} from '../../../config/constants/googleAdBanner';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {
  COMPANIES,
  COMPANY_FOLLOW_RANKING,
  HOME,
} from '../../../config/constants/routes';
import isAppWebView from '../../../config/isAppWebView';
import {
  CONSULTANT_INDUSTRIES,
  FOLLOW_COMPANY_RANKING_TERMS,
  FOLLOW_COMPANY_RANKING_TERM_LABELS,
} from '../../../core/entities/company/constants';
import {
  FollowRankingListTerm,
  type FollowRankingListTermType,
} from '../../../core/entities/company/schema';
import {useGetFollowCompanyRankingList} from '../../../core/repositories/companyRepository';
import type {IndustryCategoriesReturnValueType} from '../../../core/repositories/reportsRepository';
import {useAuthAndCounts} from '../../../core/usecases/useAuthAndCounts';
import {GoogleAdBanner} from '../../../shared/components/Banner/GoogleAdBanner';
import {createPathWithQuery} from '../../../shared/services/createPathWithQuery';
import {isDeepEqualNumberArray} from '../../../shared/services/isDeepEqual';
import {useGoogleAdBanner} from '../../../shared/services/useGoogleAdBanner';
import {CompanyListHeader} from './Container/CompanyListHeader';
import {CompanyCardFollowRanking} from './Container/Presenter/CompanyCardFollowRanking';
import {FollowRankingTagSelector} from './Container/Presenter/FollowRankingTagSelector';
import {FollowRankingTermSelector} from './Container/Presenter/FollowRankingTermSelector';

type Props = {
  industryCategories?: IndustryCategoriesReturnValueType;
};

export const FollowRankingPage: React.FC<Props> = ({industryCategories}) => {
  // DFPバナーのセット
  const googleAdBannerNames = [GoogleAdBannerName.RICH_DESKTOP_HEADER];
  useGoogleAdBanner(googleAdBannerNames);

  const router = useRouter();
  const {
    parsedTerm,
    parsedIndustryIds,
    parsedIndustryCategoryId,
    parsedIsEngineerCompany,
  } = parseQueryParams(router.query);

  const [term, setTerm] = useState<FollowRankingListTermType>(
    parsedTerm ?? FOLLOW_COMPANY_RANKING_TERMS.daily,
  );
  const [industryCategoryId, setIndustryCategoryId] = useState<number>(
    parsedIndustryCategoryId ?? 0,
  );
  const [industryIds, setIndustryIds] = useState<number[]>(
    parsedIndustryIds ?? [],
  );
  const [isEngineerCompany, setIsEngineerCompany] = useState(
    parsedIsEngineerCompany,
  );

  const {user} = useAuthAndCounts();
  const isEngineer = user?.isEngineer;

  const {data: followRanking, isLoading: isLoadingFollowRanking} =
    useGetFollowCompanyRankingList({
      limit: 30,
      industryCategoryID:
        industryCategoryId > 0 ? industryCategoryId : undefined,
      industryIDs: industryIds.length > 0 ? industryIds : undefined,
      isEngineerCompany: isEngineerCompany,
      term: term !== FOLLOW_COMPANY_RANKING_TERMS.all ? term : undefined,
    });

  const isDisplayRanking = !isLoadingFollowRanking && followRanking?.ok;

  const getPageTitle = () => {
    const pageTitle = `${FOLLOW_COMPANY_RANKING_TERM_LABELS[term]}フォロー数ランキング`;

    const industry = CONSULTANT_INDUSTRIES.filter((industry) => {
      return (
        industryIds.length === industry.ids.length &&
        new Set(industryIds).size === new Set(industry.ids).size &&
        [...new Set(industryIds)].every((n) => industry.ids.includes(n))
      );
    }).map((industry) => industry.name);

    const industryCategory = industryCategories?.ok
      ? industryCategories.value.industryCategories
          .filter((category) => {
            return industryCategoryId === category.id;
          })
          .map((industry) => industry.name)
      : '';

    if (isEngineerCompany) {
      return `エンジニア企業の${pageTitle}`;
    }

    return industry.length > 0
      ? `${industry}の${pageTitle}`
      : industryCategory.length > 0
        ? `${industryCategory}の${pageTitle}`
        : pageTitle;
  };

  const getCrumbs = () => {
    const base = [
      {label: HOME_CRUMB, href: HOME},
      {label: COMPANIES_CRUMB, href: COMPANIES},
      {label: COMPANY_FOLLOW_RANKING_CRUMB, href: COMPANY_FOLLOW_RANKING},
    ];

    return industryCategoryId === 0 &&
      industryIds.length === 0 &&
      term === FOLLOW_COMPANY_RANKING_TERMS.daily
      ? base
      : [
          ...base,
          {
            label: getPageTitle(),
            href: '',
          },
        ];
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: URLから期間・業界指定を取得する
  useEffect(() => {
    if (parsedTerm && parsedTerm !== term) {
      setTerm(parsedTerm);
    }
    if (!isDeepEqualNumberArray(parsedIndustryIds, industryIds)) {
      setIndustryIds(parsedIndustryIds);
    }
    if (parsedIndustryCategoryId !== industryCategoryId) {
      setIndustryCategoryId(parsedIndustryCategoryId);
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: 期間・業界の変更時
  useEffect(() => {
    const params: {
      term: FollowRankingListTermType;
      industry_category_id?: number;
      industry_id?: number[];
      is_engineer_company?: boolean;
    } = {term};
    if (industryIds.length > 0) {
      params.industry_id = industryIds;
    } else if (industryCategoryId > 0) {
      params.industry_category_id = industryCategoryId;
    } else if (isEngineerCompany) {
      params.is_engineer_company = isEngineerCompany;
    }

    const {
      parsedTerm,
      parsedIndustryIds,
      parsedIndustryCategoryId,
      parsedIsEngineerCompany,
    } = parseQueryParams(router.query);

    if (
      term !== parsedTerm ||
      !isDeepEqualNumberArray(industryIds, parsedIndustryIds) ||
      industryCategoryId !== parsedIndustryCategoryId ||
      isEngineerCompany !== parsedIsEngineerCompany
    ) {
      if (isAppWebView()) {
        location.href = createPathWithQuery(
          COMPANY_FOLLOW_RANKING,
          params,
          'none',
        );
      } else {
        router.push(
          createPathWithQuery(COMPANY_FOLLOW_RANKING, params, 'none'),
        );
      }
    }
  }, [term, industryCategoryId, industryIds]);

  const pageTitle = getPageTitle();
  const description =
    '今注目を集めている企業をチェック！フォロー機能で最新情報の通知を受け取ろう！';
  const url = `${GSSKT_HOST}${router.asPath}`;
  const crumbs = getCrumbs();

  return (
    <>
      <Head>
        <title key="title">{pageTitle + TITLE_SUFFIX}</title>
        <meta key="og:title" property="og:title" content={pageTitle} />
        <meta key="og:url" property="og:url" content={url} />
        <meta key="description" name="description" content={description} />
        <meta
          name="keywords"
          content="外資・日系トップ企業,企業一覧,外資,就活,インターンシップ,新卒採用,会社説明会,就活コラム,ES・体験記,注目企業"
        />
        <link rel="canonical" href={url} key="canonical" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1.0,maximum-scale=1.0"
        />
      </Head>
      <Box className="hidden pc:block">
        <GoogleAdBanner
          googleAdBannerName={GoogleAdBannerName.RICH_DESKTOP_HEADER}
        />
      </Box>
      <Box className="w-full pc:w-[1200px] mx-auto">
        <CompanyListHeader
          pageTitle={pageTitle}
          crumbs={crumbs}
          description={description}
        />
        <Box className="flex flex-col pc:flex-row justify-between mx-16 pc:mx-0 my-16 p-16 pc:p-0 gap-16 bg-white pc:bg-transparent rounded-[20px] pc:rounded-none shadow-card pc:shadow-transparent">
          <FollowRankingTagSelector
            industryCategoryId={industryCategoryId}
            setIndustryCategoryId={setIndustryCategoryId}
            industryIds={industryIds}
            setIndustryIds={setIndustryIds}
            industryCategories={industryCategories}
            isEngineerUser={isEngineer ?? false}
            isEngineerCompany={isEngineerCompany}
            setIsEngineerCompany={setIsEngineerCompany}
          />
          <FollowRankingTermSelector
            rankingTerm={term}
            setRankingTerm={setTerm}
          />
        </Box>
      </Box>
      <Box className="w-full bg-white py-24">
        <Box className="w-full pc:w-[1200px] mx-auto">
          {isDisplayRanking ? (
            followRanking.value.ranking.length > 0 ? (
              <div className="flex flex-wrap mt-8 gap-24">
                {followRanking.value.ranking.map((item) => {
                  return (
                    <CompanyCardFollowRanking
                      key={`followRanking-${item.company.id}`}
                      rankingItem={item}
                      isLarge={true}
                      isAllTerm={term === FOLLOW_COMPANY_RANKING_TERMS.all}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="h-svh px-16">
                <p className="text-center text-black-1 text-base">
                  {getPageTitle()}は0件です。
                </p>
              </div>
            )
          ) : (
            <div className="flex flex-wrap mt-8 gap-24 justify-center pc:justify-start">
              {Array.from({length: 12}).map(() => (
                <div key={`skelton-${randomId()}`}>
                  <Skeleton height={120} width={282} />
                  <Skeleton height={45} width={282} mt={6} />
                  <Skeleton height={24} width={282} mt={6} />
                </div>
              ))}
            </div>
          )}
        </Box>
      </Box>
    </>
  );
};

const parseQueryParams = (query: {
  term?: string;
  industry_id?: string | string[];
  industry_category_id?: string;
  is_engineer_company?: string;
}): {
  parsedTerm: FollowRankingListTermType | undefined;
  parsedIndustryIds: number[];
  parsedIndustryCategoryId: number;
  parsedIsEngineerCompany: boolean;
} => {
  const {
    term,
    industry_id: industryIds,
    industry_category_id: industryCategoryId,
    is_engineer_company: isEngineerCompany,
  } = query;

  let parsedTerm: FollowRankingListTermType | undefined;
  let parsedIndustryIds: number[] = [];
  let parsedIndustryCategoryId = 0;

  const parsedTermResult = FollowRankingListTerm.safeParse(term);
  if (parsedTermResult.success) {
    parsedTerm = parsedTermResult.data;
  }

  if (typeof industryIds === 'string' || !Number.isNaN(Number(industryIds))) {
    parsedIndustryIds = [Number(industryIds)];
  } else if (Array.isArray(industryIds)) {
    parsedIndustryIds = industryIds
      .map((id) => {
        if (!Number.isNaN(Number(id))) {
          return Number(id);
        }
        return undefined;
      })
      .filter((id): id is number => id !== undefined);
  }

  if (
    !Number.isNaN(Number(industryCategoryId)) &&
    parsedIndustryIds.length === 0
  ) {
    parsedIndustryCategoryId = Number(industryCategoryId);
  }

  return {
    parsedTerm,
    parsedIndustryIds,
    parsedIndustryCategoryId,
    parsedIsEngineerCompany: isEngineerCompany === 'true',
  };
};
